import { inject, Injectable } from '@angular/core';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { CookiesService } from '@amfam/shared/utility/shared-services/lib/cookies/cookies.service';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';
import { formatErrorMessage } from '@amfam/shared/utility/shared-services/lib/utils/error.util';

import { AgentInfoProfileRequestService } from './agent-info-profile-request.service';

// The only cookie this service may touch. The response names it, but it goes straight into
// document.cookie on a shared domain, so a wrong name must not reach a session or auth cookie.
const AGENT_COOKIE_NAME = 'agentID';

// The `agentCookie` part of Nexus `GET /agent-info`: an instruction to set or remove a cookie, as
// given. `value` and `expiresAt` come with `set` only.
interface AgentCookieResponse {
  response?: {
    agentCookie?: {
      action: 'set' | 'remove';
      name: string;
      value?: string;
      path: string;
      domain: string;
      // An RFC 1123 date (`Thu, 26 Oct 2028 12:00:00 GMT`, as `Date#toUTCString`). It goes straight
      // into `; expires=`, and an ISO date there makes the browser store a session cookie instead.
      expiresAt?: string;
    };
  };
}

/**
 * Keeps the `agentID` cookie amfam.com reads in step with the customer's agents, from the cookie
 * instruction in `GET /agent-info` (ADR-0001: the API decides the value; this only applies it).
 *
 * It uses the shared profile-surface request, so on /profile and the change-request page it adds
 * no call of its own. On the overview, where the page asks for `surface=overview`, this is a
 * second request with a different response; the BFF's producer lookups are cached, so it is cheap.
 */
@Injectable({ providedIn: 'root' })
export class AgentCookieService {
  private readonly profileRequest = inject(AgentInfoProfileRequestService);
  private readonly cookies = inject(CookiesService);
  private readonly utilService = inject(UtilService);
  private requested = false;

  /**
   * Safe to call on every navigation: it asks once per page load. A failure is not retried within
   * the page load, so a customer the call fails for does not send a failing request per navigation;
   * the cookie lasts two years and the next page load asks again.
   */
  sync(): void {
    if (this.requested) return;
    this.requested = true;

    this.profileRequest
      .get<AgentCookieResponse>()
      .pipe(
        catchError((error: unknown) => {
          // With the flag on this is the only writer of agentID, so a failure must be visible.
          this.utilService.error(
            '[AgentCookie] GET /agent-info failed; agentID cookie not synced',
            {
              error: formatErrorMessage(error, false)
            }
          );
          return EMPTY;
        })
      )
      .subscribe(res => {
        const cookie = res.response?.agentCookie;
        if (!cookie) return;

        if (cookie.name !== AGENT_COOKIE_NAME) {
          this.utilService.error('[AgentCookie] /agent-info named an unexpected cookie; ignored', {
            name: cookie.name
          });
          return;
        }

        if (cookie.action === 'remove') {
          this.cookies.removeItem(cookie.name, cookie.path, cookie.domain);
        } else if (cookie.value !== undefined) {
          this.cookies.setItem(
            cookie.name,
            cookie.value,
            cookie.expiresAt,
            cookie.path,
            cookie.domain
          );
        }
      });
  }
}
